import {
  Activity,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BellRing,
  Bone,
  Brain,
  Briefcase,
  Building2,
  CalendarCheck,
  CalendarDays,
  CalendarPlus,
  CircleCheck,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  CreditCard,
  Download,
  Dumbbell,
  Eye,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  Footprints,
  HandHeart,
  HardHat,
  Handshake,
  HeartPulse,
  Hospital,
  IdCard,
  LayoutDashboard,
  LockKeyhole,
  MailCheck,
  MapPin,
  Megaphone,
  MessageCircle,
  Notebook,
  PersonStanding,
  Phone,
  Pill,
  Printer,
  Receipt,
  Route,
  Scan,
  ScanLine,
  Settings,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Stethoscope,
  Sun,
  Sunrise,
  Sunset,
  Target,
  TrendingUp,
  Trash2,
  TriangleAlert,
  Upload,
  User,
  UserRound,
  Users,
  UserX,
  Video,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react'

/**
 * Set de íconos de la marca (H8). Reemplaza los emojis que se usaban como
 * íconos: los emoji cambian de forma y color según el sistema operativo, no
 * heredan el color de marca y se ven como decoración, no como interfaz.
 *
 * Todos son SVG de trazo (lucide-react), con el mismo grosor de línea para que
 * el conjunto se lea como una familia. El color se hereda de `currentColor`,
 * así que se pintan con las clases de Tailwind de la paleta (`text-brand-600`,
 * `text-steel-500`…). Se re-exportan desde aquí para tener un único lugar donde
 * ver y cambiar el vocabulario visual del producto.
 */
export type { LucideIcon }

/** Grosor de trazo del set. Fino y moderno, legible desde 16 px. */
export const ICON_STROKE = 1.75

export const Icons = {
  // --- Navegación del panel ---
  dashboard: LayoutDashboard,
  agenda: CalendarDays,
  patients: Users,
  leads: Target,
  cashier: CreditCard,
  preoccupational: HardHat,
  quotes: FileSpreadsheet,
  accounting: Notebook,
  fees: Receipt,
  production: TrendingUp,
  blog: FileText,
  settings: Settings,

  // --- Clínico / servicios ---
  sportsMedicine: Stethoscope,
  trauma: Bone,
  surgery: HandHeart,
  rehab: Dumbbell,
  imaging: ScanLine,
  mri: Brain,
  checkup: ClipboardList,
  prescription: Pill,
  lab: FlaskConical,
  favorite: Star,
  injury: Footprints,
  athlete: Activity,
  vitals: HeartPulse,
  hospital: Hospital,

  // --- Negocio / empresas ---
  company: Building2,
  quote: Briefcase,
  agreement: Handshake,
  certificate: BadgeCheck,
  shield: ShieldCheck,

  // --- Contacto y acciones ---
  phone: Phone,
  mobile: Smartphone,
  whatsapp: MessageCircle,
  location: MapPin,
  clock: Clock,
  scheduleCheck: CalendarCheck,
  print: Printer,
  callPatient: Megaphone,
  bell: BellRing,
  noShow: UserX,
  scan: Scan,
  route: Route,
  video: Video,
  fast: Zap,
  spark: Sparkles,
  person: User,
  personRound: UserRound,

  // --- Documentos del paciente ---
  upload: Upload,
  download: Download,
  view: Eye,
  trash: Trash2,
  document: FileText,

  // --- Estado y navegación ---
  check: Check,
  close: X,
  warning: TriangleAlert,
  arrowRight: ArrowRight,
  arrowLeft: ArrowLeft,
  next: ChevronRight,
  prev: ChevronLeft,

  // Agendamiento en línea
  posture: PersonStanding,
  morning: Sunrise,
  afternoon: Sunset,
  allDay: Sun,
  idCard: IdCard,
  mailCheck: MailCheck,
  circleCheck: CircleCheck,
  calendarPlus: CalendarPlus,
  lock: LockKeyhole,
} satisfies Record<string, LucideIcon>

export type IconName = keyof typeof Icons

/** Renderiza un ícono del set por nombre, con el trazo de la familia. */
export function Icon({
  name,
  className = 'h-5 w-5',
  strokeWidth = ICON_STROKE,
}: {
  name: IconName
  className?: string
  strokeWidth?: number
}) {
  const Cmp = Icons[name]
  return <Cmp className={className} strokeWidth={strokeWidth} aria-hidden />
}
